// @ts-nocheck
/*
 * Port of views/dashboard/agent-call-stats/index.js.
 * APIs: API_AGENT_CALL_SUMMARY_CARDS + API_AGENT_CALL_STATS.
 * Byte-identical logic:
 *   - 5 fixed period summary cards (today, yesterday, this_week, this_month, last_month)
 *   - Filter by date + agent (3-month rolling window)
 *   - Sections: "Answered / Missed / Declined per Agent" (apex bar),
 *     "Overall Distribution" (apex donut), "Per-Agent Breakdown" (table).
 * Column count preserved: 12 (Agent, Total, Answered, Unanswered, Missed,
 * Declined, Answered %, Unanswered %, Missed %, Declined %, Total Wrap, Avg Wrap).
 */
import { useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall } from '@/service/httpCall';
import { API_AGENT_CALL_SUMMARY_CARDS, API_AGENT_CALL_STATS } from '@/service/url';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/data-table/DataTable';
import CallQualityMetricsPanel from '@/features/call-quality/CallQualityMetricsPanel';
import { SUCCESS_COLOR, WARNING_COLOR, FAILURE_COLOR } from '../chart-palette';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

const pad = (n) => String(n).padStart(2, '0');
const toYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const today = () => new Date();
const firstOfMonth = () => {
  const d = today();
  return new Date(d.getFullYear(), d.getMonth(), 1);
};
const minusMonths = (d, m) => {
  const x = new Date(d);
  x.setMonth(x.getMonth() - m);
  return x;
};

const formatDuration = (totalSeconds) => {
  const s = Number(totalSeconds) || 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${pad(m)}m ${pad(sec)}s`;
  return `${m}m ${pad(sec)}s`;
};

const CARD_ACCENTS = {
  today: '#4285FA',
  yesterday: '#7E57C2',
  this_week: '#26A69A',
  this_month: '#FB8C00',
  last_month: '#EC407A',
};

const PERIOD_FALLBACK = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this_week', label: 'This Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
];

export default function AgentCallStats() {
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);

  const [startDate, setStartDate] = useState(toYMD(firstOfMonth()));
  const [endDate, setEndDate] = useState(toYMD(today()));
  const [agentId, setAgentId] = useState('');

  const [agentOptions, setAgentOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const maxDate = toYMD(today());
  const minStartDate = useMemo(
    () => toYMD(minusMonths(new Date(endDate || maxDate), 3)),
    [endDate, maxDate],
  );

  useEffect(() => {
    let mounted = true;
    setCardsLoading(true);
    getApiCall(API_AGENT_CALL_SUMMARY_CARDS)
      .then((resp) => {
        if (!mounted) return;
        setCards(Array.isArray(resp?.periods) ? resp.periods : []);
      })
      .catch(() => mounted && setCards([]))
      .finally(() => mounted && setCardsLoading(false));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    setTableLoading(true);
    setError('');
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (agentId) params.append('agent_id', agentId);

    getApiCall(`${API_AGENT_CALL_STATS}?${params.toString()}`)
      .then((resp) => {
        if (!mounted) return;
        setRows(Array.isArray(resp?.agents) ? resp.agents : []);
        setTotals(resp?.totals || null);
        if (Array.isArray(resp?.agent_options)) setAgentOptions(resp.agent_options);
      })
      .catch((err) => {
        if (!mounted) return;
        setRows([]);
        setTotals(null);
        setError(typeof err === 'string' ? err : 'Failed to load agent call stats');
      })
      .finally(() => mounted && setTableLoading(false));
    return () => { mounted = false; };
  }, [startDate, endDate, agentId]);

  const handleStartChange = (value) => {
    if (!value) return;
    let next = value;
    if (next < minStartDate) next = minStartDate;
    if (endDate && next > endDate) next = endDate;
    setStartDate(next);
  };

  const handleEndChange = (value) => {
    if (!value) return;
    let next = value;
    if (next > maxDate) next = maxDate;
    setEndDate(next);
    const newMin = toYMD(minusMonths(new Date(next), 3));
    if (startDate < newMin) setStartDate(newMin);
    if (startDate > next) setStartDate(next);
  };

  const barChart = useMemo(() => {
    const top = [...rows].sort((a, b) => b.total - a.total).slice(0, 15);
    return {
      options: {
        chart: { type: 'bar', stacked: true, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: false, borderRadius: 4, columnWidth: '55%' } },
        xaxis: { categories: top.map((r) => r.agent_name || `#${r.agent_id}`) },
        legend: { position: 'top' },
        colors: [SUCCESS_COLOR, WARNING_COLOR, FAILURE_COLOR],
        dataLabels: { enabled: false },
        tooltip: { shared: true, intersect: false },
      },
      series: [
        { name: 'Answered', data: top.map((r) => r.answered) },
        { name: 'Missed', data: top.map((r) => r.missed) },
        { name: 'Declined', data: top.map((r) => r.declined) },
      ],
    };
  }, [rows]);

  const donut = useMemo(() => ({
    options: {
      chart: { type: 'donut' },
      labels: ['Answered', 'Missed', 'Declined'],
      legend: { position: 'bottom' },
      colors: [SUCCESS_COLOR, WARNING_COLOR, FAILURE_COLOR],
      dataLabels: { enabled: true },
    },
    series: [totals?.answered || 0, totals?.missed || 0, totals?.declined || 0],
  }), [totals]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const paged = rows.slice(page * pageSize, page * pageSize + pageSize);

  const displayCards = cardsLoading ? PERIOD_FALLBACK : cards.length ? cards : PERIOD_FALLBACK;

  const perAgentColumns = useMemo<ColumnDef<any>[]>(
    () => [
      { accessorKey: 'agent_name', header: 'Agent' },
      { accessorKey: 'total', header: 'Total' },
      { accessorKey: 'answered', header: 'Answered' },
      { accessorKey: 'unanswered', header: 'Unanswered' },
      { accessorKey: 'missed', header: 'Missed' },
      { accessorKey: 'declined', header: 'Declined' },
      { accessorKey: 'answered_pct', header: 'Answered %', cell: ({ row }) => `${Number(row.original.answered_pct || 0).toFixed(2)}%` },
      { accessorKey: 'unanswered_pct', header: 'Unanswered %', cell: ({ row }) => `${Number(row.original.unanswered_pct || 0).toFixed(2)}%` },
      { accessorKey: 'missed_pct', header: 'Missed %', cell: ({ row }) => `${Number(row.original.missed_pct || 0).toFixed(2)}%` },
      { accessorKey: 'declined_pct', header: 'Declined %', cell: ({ row }) => `${Number(row.original.declined_pct || 0).toFixed(2)}%` },
      { accessorKey: 'total_wrap_seconds', header: 'Total Wrap', cell: ({ row }) => formatDuration(row.original.total_wrap_seconds) },
      { accessorKey: 'avg_wrap_seconds', header: 'Avg Wrap', cell: ({ row }) => formatDuration(row.original.avg_wrap_seconds) },
    ],
    [],
  );

  return (
    <div className="mb-6">
      <h4 className="mb-3 text-lg font-bold text-foreground">Agent Call Statistics</h4>

      {/* 5 fixed period summary cards */}
      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {displayCards.map((c) => (
          <Card key={c.key} className="p-4">
            <div className="border-l-[3px] pl-3" style={{ borderLeftColor: CARD_ACCENTS[c.key] || '#4285FA' }}>
              <div className="text-xs font-semibold text-muted-foreground">{c.label}</div>
              <div className="text-2xl font-bold text-foreground">{c.total ?? 0}</div>
              <div className="mt-1 space-y-0.5 text-[12px]">
                <div className="flex justify-between"><span className="text-emerald-600">Answered</span><span>{c.answered ?? 0} ({(c.answered_pct ?? 0)}%)</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Unanswered</span><span>{c.unanswered ?? 0} ({(c.unanswered_pct ?? 0)}%)</span></div>
                <div className="flex justify-between"><span className="text-amber-600">Missed</span><span>{c.missed ?? 0} ({(c.missed_pct ?? 0)}%)</span></div>
                <div className="flex justify-between"><span className="text-red-500">Declined</span><span>{c.declined ?? 0} ({(c.declined_pct ?? 0)}%)</span></div>
                <hr className="my-1 border-border" />
                <div className="flex justify-between"><span className="text-muted-foreground">Total Wrap</span><span>{formatDuration(c.total_wrap_seconds)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Avg Wrap</span><span>{formatDuration(c.avg_wrap_seconds)}</span></div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="mb-3 p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Start Date</label>
            <CalendarDateInput
              value={startDate}
              onChange={(e) => handleStartChange(e)}
              min={minStartDate}
              max={endDate || maxDate}
              className="h-9"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">End Date</label>
            <CalendarDateInput
              value={endDate}
              onChange={(e) => handleEndChange(e)}
              max={maxDate}
              className="h-9"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Agent</label>
            <select
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-2 text-sm"
            >
              <option value="">All Agents</option>
              {agentOptions.map((a) => (
                <option key={a.id} value={a.id}>{a.agent_name || `Agent #${a.id}`}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Charts */}
      <div className="mb-3 grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Card className="p-4 lg:col-span-2">
          <h5 className="mb-2 text-sm font-bold text-foreground">Answered / Missed / Declined per Agent</h5>
          {rows.length ? (
            <Chart options={barChart.options} series={barChart.series} type="bar" height={320} />
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">No data for selected range</div>
          )}
        </Card>
        <Card className="p-4">
          <h5 className="mb-2 text-sm font-bold text-foreground">Overall Distribution</h5>
          {totals && totals.total > 0 ? (
            <Chart options={donut.options} series={donut.series} type="donut" height={320} />
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">No data for selected range</div>
          )}
        </Card>
      </div>

      {/* Per-agent table — 12 columns via obd3-gui DataTable */}
      <Card className="p-4">
        <h5 className="mb-2 text-sm font-bold text-foreground">Per-Agent Breakdown</h5>
        {error ? <div className="mb-2 text-sm text-destructive">{error}</div> : null}
        <DataTable
          tableId="dashboard.agent-call-stats.per-agent"
          columns={perAgentColumns}
          data={rows}
          isLoading={tableLoading}
          emptyTitle="No agent call data"
          emptyDescription="No agent call data for the selected range."
          pageSize={10}
          exportFilename="agent-call-stats.csv"
        />
        {false && (
          <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
            <span>Page {page + 1} of {totalPages} · {rows.length} rows</span>
            <div className="flex gap-2">
              <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="rounded border border-border px-2 py-1 disabled:opacity-40">Prev</button>
              <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} className="rounded border border-border px-2 py-1 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </Card>

      {/*
       * WebRTC Call Quality panel — rendered inline below Per-Agent Breakdown
       * exactly as origin/main-dev's AgentCallStats does (line 344 of the JS
       * source). Same agent filter, same date range. Its own `to` gets the
       * 23:59:59 suffix so the backend's date compare is inclusive of the
       * whole end day, verbatim from the JS.
       */}
      <div className="mt-3">
        <CallQualityMetricsPanel
          agentId={agentId || null}
          from={startDate}
          to={`${endDate} 23:59:59`}
        />
      </div>
    </div>
  );
}
